/**
 * VoicePage — Route wrapper for Voice Chat
 *
 * File: Mythbound.App/src/pages/VoicePage.tsx
 * Created: 2026-02-24
 * Last Modified: 2026-02-24
 *
 * PURPOSE:
 *   Provides a full-page route (/voice) instead of the previous <NotFound />.
 *   VoicePanel requires an active session (sessionId + participantId), so this
 *   page shows session-aware content: if no session is active, it guides the
 *   user to start one from their campaigns.
 *
 * WHY:
 *   The "Join Voice" Quick Action on the Dashboard navigates to /voice.
 *   VoicePanel was fully built (v0.0.0897) but requires session context.
 *
 * INTEGRATION POINT:
 *   When session state is available in context, render <VoicePanel /> directly
 *   with sessionId and participantId from the active session.
 *
 * Pipeline: Dashboard "Join Voice" → navigate('/voice') → VoicePage
 */

import React from 'react';
import { Box, Heading, Text, VStack, Button, Center, Card, CardBody, Icon } from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { theme as mythboundTheme } from '../config/theme';

const VoicePage: React.FC = () => {
  const navigate = useNavigate();

  // TODO: When SessionContext is available, check for active session
  // and render <VoicePanel sessionId={...} participantId={...} /> instead.
  return (
    <Box p={4}>
      <Heading
        size="lg"
        mb={6}
        fontFamily={mythboundTheme.typography.fontFamily.heading}
        color={mythboundTheme.colors.primary.gold}
      >
        Voice Chat
      </Heading>

      <Center py={12}>
        <Card
          w="full"
          maxW="460px"
          textAlign="center"
          bg="rgba(59, 232, 255, 0.05)"
          borderWidth="1px"
          borderColor={mythboundTheme.colors.accent.teal}
        >
          <CardBody>
            <VStack spacing={5}>
              <Box fontSize="4xl">
                🎙️
              </Box>
              <VStack spacing={2}>
                <Heading
                  size="md"
                  fontFamily={mythboundTheme.typography.fontFamily.heading}
                  color={mythboundTheme.colors.secondary.white}
                >
                  Start a Session to Join Voice
                </Heading>
                <Text
                  color={mythboundTheme.colors.accent.silver}
                  fontFamily={mythboundTheme.typography.fontFamily.body}
                >
                  Voice chat activates during game sessions. Head to your campaigns
                  to start or join a session.
                </Text>
              </VStack>
              <Button
                colorScheme="blue"
                onClick={() => navigate('/campaigns')}
                mt={2}
                fontFamily={mythboundTheme.typography.fontFamily.heading}
              >
                Go to Campaigns
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Center>
    </Box>
  );
};

export default VoicePage;
